/** Typed Learnosity startup failures, kept free of imports so telemetry and loaders can share them. */

export class LearnosityReportAccessDeniedError extends Error {
  constructor() {
    super('Learnosity report access denied');
    this.name = 'LearnosityReportAccessDeniedError';
  }
}

export class LearnosityInitializationPayloadError extends Error {
  constructor(kind: 'invalid' | 'missing' = 'invalid') {
    super(
      kind === 'invalid'
        ? 'Invalid Learnosity initialization payload'
        : 'Learnosity signing refresh returned no request',
    );
    this.name = 'LearnosityInitializationPayloadError';
  }
}

export class LearnositySdkTimeoutError extends Error {
  constructor(readonly phase: 'initialization' | 'sdk_reload') {
    super(`Learnosity startup blocked by unsettled ${phase} after its startup deadline`);
    this.name = 'LearnositySdkTimeoutError';
  }
}

export class LearnosityItemsSdkError extends Error {
  constructor(readonly kind: 'bootstrap_unavailable' | 'reload_failed' | 'reload_blocked') {
    super(
      kind === 'bootstrap_unavailable'
        ? 'Learnosity Items bootstrap did not publish its SDK'
        : kind === 'reload_failed'
        ? 'Learnosity Items SDK script reload failed'
        : 'Learnosity Items SDK reload blocked by another active initialization',
    );
    this.name = 'LearnosityItemsSdkError';
  }
}

export class LearnosityRetryMismatchError extends Error {
  constructor() {
    super('Learnosity retry initialization changed assessment or session');
    this.name = 'LearnosityRetryMismatchError';
  }
}

/**
 * Items core v1.132.5 loads exactly one rendering dependency per activity:
 * `assessApiPath` for rendering_type 'assess', `questionsApiV2Path` for 'inline'.
 * The two are mutually exclusive, so a failure in one host leaves the other usable.
 */
export const LEARNOSITY_RENDERING_DEPENDENCY_HOST: Record<string, string> = {
  assess: 'assess.learnosity.com',
  inline: 'questions.learnosity.com',
};

function scriptFailureUrl(error: unknown): URL | undefined {
  if (!error || typeof error !== 'object' || !('msg' in error) || typeof error.msg !== 'string') return;
  const match = /^Failed loading script: (https:\/\/\S+) Reason:/.exec(error.msg);
  if (!match) return;
  try {
    return new URL(match[1]);
  } catch {
    return;
  }
}

/** The rendering dependency host a script failure names, when it names one. */
export function learnosityScriptFailureHost(error: unknown): string | undefined {
  const host = scriptFailureUrl(error)?.hostname;
  return host && Object.values(LEARNOSITY_RENDERING_DEPENDENCY_HOST).includes(host) ? host : undefined;
}

/** A bootstrap or dependency-script failure reported through an Items callback. */
export function isLearnosityScriptFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) return false;
  const code = String(error.code);
  if (code === '50014') return true;
  if (code !== '50004') return false;
  const url = scriptFailureUrl(error);
  return !!url && /^(items|assess|questions|events|annotations)\.learnosity\.com$/.test(url.hostname);
}
